import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { teamsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { Users, CheckCircle, XCircle, AlertCircle, Loader2, LogOut } from 'lucide-react';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();

  const [status, setStatus] = useState('loading'); // loading, ready, preauth, success, error, declined
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [inviteDetails, setInviteDetails] = useState(null);
  const autoAcceptedRef = useRef(false);

  // Load invite details regardless of auth (public endpoint)
  useEffect(() => {
    teamsApi.getInviteDetails(token)
      .then(res => {
        setInviteDetails(res.data);
      })
      .catch(err => {
        const detail = err.response?.data?.detail || 'Invitation not found or has expired';
        setMessage(detail);
        setStatus('error');
      });
  }, [token]);

  // Once auth state + invite details settle, decide UI
  useEffect(() => {
    if (authLoading) return;
    if (status === 'error') return;
    if (!inviteDetails) return;

    if (!isAuthenticated) {
      setStatus('preauth');
      return;
    }

    // Authenticated: if email matches and invite is pending, auto-accept.
    const emailMatches = user && inviteDetails.email.toLowerCase() === user.email.toLowerCase();
    if (emailMatches && inviteDetails.status === 'pending' && !autoAcceptedRef.current) {
      autoAcceptedRef.current = true;
      handleAccept();
      return;
    }

    setStatus('ready');
  }, [authLoading, isAuthenticated, user, inviteDetails]); // eslint-disable-line

  const handleAccept = async () => {
    setProcessing(true);
    try {
      await teamsApi.acceptInvite(token);
      setStatus('success');
      setMessage('You have successfully joined the team!');
      // axios interceptor invalidates the teams query - the sidebar will
      // refetch automatically when AppLayout next mounts.
      setTimeout(() => navigate('/app/settings'), 2000);
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      setMessage((typeof detail === 'object' ? detail?.message : detail) || 'Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    setProcessing(true);
    try {
      await teamsApi.declineInvite(token);
      setStatus('declined');
      setMessage('Invitation declined');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleSwitchAccount = async () => {
    const params = new URLSearchParams({
      redirect: `/invite/${token}`,
      email: inviteDetails?.email || '',
    });
    logout?.(`/login?${params.toString()}`);
  };

  const inviteQuery = inviteDetails
    ? `?${new URLSearchParams({ redirect: `/invite/${token}`, email: inviteDetails.email }).toString()}`
    : `?redirect=/invite/${token}`;

  const isWrongAccount = inviteDetails && user &&
    inviteDetails.email.toLowerCase() !== user.email.toLowerCase();

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <BusyBeeLogo className="h-12 mx-auto" />
          </Link>
        </div>

        <div className="bg-white/5 rounded-2xl border border-gray-800 p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">Loading...</h2>
            </>
          )}

          {status === 'preauth' && inviteDetails && (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Team Invitation</h2>
              <p className="text-gray-400 mb-2">
                You've been invited to join <span className="text-white font-medium">{inviteDetails.team_name}</span>
                {inviteDetails.invited_by_name && (
                  <> by <span className="text-white">{inviteDetails.invited_by_name}</span></>
                )}
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Sent to <span className="text-white">{inviteDetails.email}</span>
              </p>
              <div className="space-y-3">
                <Link to={`/register${inviteQuery}`} className="block">
                  <Button className="w-full bg-amber-500 text-black hover:bg-amber-400">
                    Create account & join
                  </Button>
                </Link>
                <Link to={`/login${inviteQuery}`} className="block">
                  <Button variant="outline" className="w-full border-gray-700 text-gray-300">
                    I already have an account
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'ready' && (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Team Invitation</h2>
              {inviteDetails && (
                <p className="text-gray-400 mb-2">
                  You've been invited to join <span className="text-white font-medium">{inviteDetails.team_name}</span>
                  {inviteDetails.invited_by_name && (
                    <> by <span className="text-white">{inviteDetails.invited_by_name}</span></>
                  )}
                </p>
              )}

              {/* Wrong account warning */}
              {isWrongAccount ? (
                <div className="mt-4 mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-left">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-300 font-medium text-sm mb-1">Wrong account</p>
                      <p className="text-gray-400 text-sm">
                        This invitation was sent to <span className="text-white">{inviteDetails.email}</span>.
                        You are currently logged in as <span className="text-white">{user.email}</span>.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleSwitchAccount}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 text-sm transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Switch to correct account
                  </button>
                </div>
              ) : (
                user && (
                  <p className="text-gray-500 text-sm mb-6">
                    Logged in as <span className="text-white">{user.email}</span>
                  </p>
                )
              )}

              {!isWrongAccount && (
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300"
                    disabled={processing}
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="flex-1 bg-amber-500 text-black hover:bg-amber-400"
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Accept Invitation'}
                  </Button>
                </div>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to the team!</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <p className="text-gray-500 text-sm">Redirecting to Teams...</p>
            </>
          )}

          {status === 'declined' && (
            <>
              <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">Invitation Declined</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <Link to="/">
                <Button variant="outline" className="border-gray-700 text-gray-300">
                  Go to Homepage
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
              <p className="text-red-400 mb-6">{message}</p>
              <Link to="/">
                <Button variant="outline" className="border-gray-700 text-gray-300">
                  Go to Homepage
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
